import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import type { ApiCounter, Enum } from '@Model';
import { type Column, type GroupedSelectOption, useServerListApi } from '@synqo/frontend-component-library';
import { EnumFrontendLayoutSetting } from '@Model';
import { useMemo } from 'react';

export interface FrameListCounterProps<TCounterType> {
    apiResourcePath: string;
    moduleName: string;
    displayCounterType: (enumValue: TCounterType) => string;
    counterTypeFilterOptions: GroupedSelectOption<string | number>[];
    createPermission: Permission;
}

export function FrameListCounter<TCounterType extends Enum>(props: FrameListCounterProps<TCounterType>) {
    const hasPermission = useHasPermission();
    const displayCounterType = props.displayCounterType;
    const counterTypeFilterOptions = props.counterTypeFilterOptions;

    const counterColumns: Column<ApiCounter<TCounterType>>[] = useMemo(
        () => [
            {
                name: 'counter_type',
                label: 'Zählertyp',
                filterVariant: 'select',
                filterOptions: counterTypeFilterOptions,
                renderCell: ({ cellData }) => displayCounterType(cellData as TCounterType),
            },
            { name: 'prefix_template', label: 'Präfix' },
            { name: 'start_counter', label: 'Anfangswert' },
            { name: 'minimum_digits', label: 'Länge' },
            { name: 'valid_from', label: 'Gültig ab', type: 'date', filterVariant: 'day-range' },
        ],
        [displayCounterType, counterTypeFilterOptions],
    );

    const module = useServerListApi<ApiCounter<TCounterType>>({
        path: props.apiResourcePath,
    });

    return (
        <TableWithStoredSettingsAndLayoutSettings
            layoutSettingsContext={EnumFrontendLayoutSetting.CountersTableLayout}
            name={`${props.moduleName}List`}
            columns={counterColumns}
            loader={module.loadPage}
            canCreate={hasPermission(props.createPermission)}
            useOnClickNavigation={true}
            fullSize
        />
    );
}
